import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building, Layers, Filter, X, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { snagStatusVariant, snagPriorityVariant } from '@/lib/status-variants'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ALL_STATUSES,
  ALL_PRIORITIES,
  Status,
  Priority,
  SnagsFilters,
} from '../../../hooks/useSnagsFilters'

interface BuildingItem {
  id: string
  name: string
  code: string | null
  floors: Floor[]
}

interface Floor {
  id: string
  name: string
  level: number
  buildingName?: string
}

interface SnagsFilterBarProps {
  filters: SnagsFilters
  onFiltersChange: (filters: SnagsFilters) => void
  buildings: BuildingItem[]
  infrastructureTypes: string[]
  showBuildingSelector?: boolean
  showFloorSelector?: boolean
  showSearch?: boolean
}

const SnagsFilterBar: React.FC<SnagsFilterBarProps> = ({
  filters,
  onFiltersChange,
  buildings,
  infrastructureTypes,
  showBuildingSelector = true,
  showFloorSelector = true,
  showSearch = false,
}) => {
  const { t } = useTranslation()
  const [filterDialogOpen, setFilterDialogOpen] = useState(false)

  // Get floors for selected building or all floors
  const getFloors = (): Floor[] => {
    if (filters.buildingId) {
      const building = buildings.find((b) => b.id === filters.buildingId)
      return building?.floors || []
    }
    const allFloors: Floor[] = []
    buildings.forEach((building) => {
      building.floors?.forEach((floor) => {
        allFloors.push({ ...floor, buildingName: building.name })
      })
    })
    return allFloors
  }

  const floors = getFloors()

  // Handlers
  const handleBuildingChange = (value: string) => {
    const buildingId = value === '__all__' ? null : value
    const building = buildingId ? buildings.find((b) => b.id === buildingId) : null
    const firstFloorId = building?.floors?.[0]?.id || null
    onFiltersChange({
      ...filters,
      buildingId,
      floorId: buildingId ? firstFloorId : null,
    })
  }

  const handleFloorChange = (value: string) => {
    const floorId = value === '__all__' ? null : value
    onFiltersChange({ ...filters, floorId })
  }

  // Toggle functions
  const toggleStatus = (status: Status) => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status]
    onFiltersChange({ ...filters, statuses: newStatuses })
  }

  const togglePriority = (priority: Priority) => {
    const newPriorities = filters.priorities.includes(priority)
      ? filters.priorities.filter((p) => p !== priority)
      : [...filters.priorities, priority]
    onFiltersChange({ ...filters, priorities: newPriorities })
  }

  const toggleInfrastructureType = (type: string) => {
    const newTypes = filters.infrastructureTypes.includes(type)
      ? filters.infrastructureTypes.filter((t) => t !== type)
      : [...filters.infrastructureTypes, type]
    onFiltersChange({ ...filters, infrastructureTypes: newTypes })
  }

  const clearAllFilters = () => {
    onFiltersChange({
      ...filters,
      statuses: [],
      priorities: [],
      infrastructureTypes: [],
      searchQuery: '',
    })
  }

  const activeStatuses = filters.statuses
  const activePriorities = filters.priorities
  const activeTypes = filters.infrastructureTypes

  const hasActiveFilters =
    activeStatuses.length > 0 ||
    activePriorities.length > 0 ||
    activeTypes.length > 0

  const activeFilterCount = activeStatuses.length + activePriorities.length + activeTypes.length

  return (
    <div className="flex items-center flex-wrap gap-2">
      {/* Building Selector */}
      {showBuildingSelector && buildings.length > 0 && (
        <Select value={filters.buildingId || '__all__'} onValueChange={handleBuildingChange}>
          <SelectTrigger className="h-8 w-auto min-w-[150px] gap-2 text-sm">
            <Building className="h-4 w-4 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('Snags_all_buildings')}</SelectItem>
            {buildings.map((building) => (
              <SelectItem key={building.id} value={building.id}>
                {building.code ? `${building.code} - ${building.name}` : building.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Floor Selector */}
      {showFloorSelector && (
        <Select value={filters.floorId || '__all__'} onValueChange={handleFloorChange}>
          <SelectTrigger className="h-8 w-auto min-w-[160px] gap-2 text-sm">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('Snags_all_floors')}</SelectItem>
            {floors.map((floor) => (
              <SelectItem key={floor.id} value={floor.id}>
                {floor.buildingName ? `${floor.buildingName} - ${floor.name}` : `${floor.name} (L${floor.level})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Separator */}
      <Separator orientation="vertical" className="h-5" />

      {/* Filter Button */}
      <Button
        variant="outline"
        size="sm"
        className="h-8 relative"
        onClick={() => setFilterDialogOpen(true)}
      >
        <Filter className="h-4 w-4" />
        {activeFilterCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground px-1">
            {activeFilterCount}
          </span>
        )}
      </Button>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              {t('Snags_filter_title', 'Filters')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* Status Section */}
            <div>
              <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                {t('Snags_filter_status')}
              </span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {ALL_STATUSES.map((status) => {
                  const isActive = filters.statuses.includes(status)
                  return (
                    <Badge
                      key={status}
                      variant={isActive ? snagStatusVariant(status) : 'secondary'}
                      className="cursor-pointer transition-colors"
                      onClick={() => toggleStatus(status)}
                    >
                      {t(`Snags_status_${status.toLowerCase()}`)}
                    </Badge>
                  )
                })}
              </div>
            </div>

            {/* Priority Section */}
            <div>
              <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                {t('Snags_filter_priority')}
              </span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {ALL_PRIORITIES.map((priority) => {
                  const isActive = filters.priorities.includes(priority)
                  return (
                    <Badge
                      key={priority}
                      variant={isActive ? snagPriorityVariant(priority) : 'secondary'}
                      className="cursor-pointer transition-colors"
                      onClick={() => togglePriority(priority)}
                    >
                      {t(`Snags_priority_${priority.toLowerCase()}`)}
                    </Badge>
                  )
                })}
              </div>
            </div>

            {/* Infrastructure Types Section */}
            {infrastructureTypes.length > 0 && (
              <div>
                <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                  {t('Snags_filter_by_type')}
                </span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {infrastructureTypes.map((type) => {
                    const isActive = filters.infrastructureTypes.includes(type)
                    return (
                      <Badge
                        key={type}
                        variant={isActive ? 'info' : 'secondary'}
                        className="cursor-pointer transition-colors"
                        onClick={() => toggleInfrastructureType(type)}
                      >
                        {type}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t">
              {hasActiveFilters ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={clearAllFilters}
                >
                  <X className="h-3 w-3" />
                  {t('Snags_clear_filters')}
                </Button>
              ) : (
                <div />
              )}
              <Button size="sm" onClick={() => setFilterDialogOpen(false)}>
                {t('Common_apply', 'Apply')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Filter Badges (inline removable) */}
      {activeStatuses.map((status) => (
        <Badge
          key={`active-${status}`}
          variant={snagStatusVariant(status)}
          className="cursor-pointer gap-1"
          onClick={() => toggleStatus(status)}
        >
          {t(`Snags_status_${status.toLowerCase()}`)}
          <X className="h-3 w-3" />
        </Badge>
      ))}

      {activePriorities.map((priority) => (
        <Badge
          key={`active-${priority}`}
          variant={snagPriorityVariant(priority)}
          className="cursor-pointer gap-1"
          onClick={() => togglePriority(priority)}
        >
          {t(`Snags_priority_${priority.toLowerCase()}`)}
          <X className="h-3 w-3" />
        </Badge>
      ))}

      {activeTypes.map((type) => (
        <Badge
          key={`active-type-${type}`}
          variant="info"
          className="cursor-pointer gap-1"
          onClick={() => toggleInfrastructureType(type)}
        >
          {type}
          <X className="h-3 w-3" />
        </Badge>
      ))}

      {/* Spacer */}
      <div className="flex-grow" />

      {/* Search Input */}
      {showSearch && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('Snags_search_placeholder')}
            value={filters.searchQuery}
            onChange={(e) => onFiltersChange({ ...filters, searchQuery: e.target.value })}
            className="h-8 w-[180px] pl-8 text-sm"
          />
          {filters.searchQuery && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => onFiltersChange({ ...filters, searchQuery: '' })}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default SnagsFilterBar
